import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  Fragment,
  type MouseEvent,
  type ReactNode,
} from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  CircleAlert,
  ExternalLink,
  FileText,
  Loader2,
  Package,
  Pencil,
  RefreshCw,
  Search,
  Send,
  XCircle,
} from "lucide-react";

import { filterPaperworkShipments } from "./paperworkSearch";
import { Button } from "@/components/ui/Button";
import { Card, CardBody } from "@/components/ui/Card";
import { CopyToClipboardGlyph } from "@/components/ui/CopyToClipboardGlyph";
import { HubContactAutocomplete } from "@/components/ui/HubContactAutocomplete";
import { HubContactTokenInput } from "@/components/ui/HubContactTokenInput";
import { Input, Label, TextArea } from "@/components/ui/Input";
import { ensureHubContactSelection, hubContactSuggestionStub, type HubContactSelection } from "@/lib/hubContactsApi";
import { HoverHintTrigger, PageTitleWithHelp, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { HoverHintDefinitionGrid } from "@/components/ui/HoverHintDefinitionGrid";
import { EntityPeekHint } from "@/components/hub/EntityPeekHint";
import {
  BatchDetailLink,
  GosiDetailLink,
  SalesOrderDetailLink,
  ShipmentDetailLink,
} from "@/components/hub/HubEntityDetailLink";
import { buildShippingGosiDetailsPath } from "@/lib/hubEntityDetailPaths";
import { apiFetch, ApiError } from "@/lib/api";
import {
  freshdeskPurchaseOrderAgentUrl,
  freshdeskTicketIdFromPurchaseOrder,
  purchaseOrderNumbersFromPreview,
} from "@/lib/freshdeskAgentUrl";
import { workOrderHubHref } from "@/pages/manufacturing/workOrdersPageModel";
import { WorkOrderDetailAnchoredCallout } from "@/pages/manufacturing/WorkOrderDetailAnchoredCallout";
import {
  PAPERWORK_PROCESS_WO_ANCHOR_ATTR,
  paperworkProcessWoAnchorKey,
  prefetchWorkOrderDetail,
  prefetchWorkOrdersForPaperworkShipment,
} from "@/pages/manufacturing/workOrderDetailWorkspace";
import { cn } from "@/lib/utils";
import {
  FALLBACK_MEGAVENTORY_PAYMENT_TERMS_LABELS,
  mapMegaventoryPaymentTermsToLabel,
} from "@/lib/megaventory-payment-terms-helpers";

interface PaperworkWirelessPortalCountRow {
  portal_org_name: string;
  portal_group_names: string;
  device_count: number;
}

interface PaperworkBatchItem {
  batch_item_id: number;
  sku: string;
  quantity: number;
  build_order_id: number | null;
  build_order_number: string | null;
  mv_number: string | null;
  mv_status: string | null;
  mv_ordered_quantity: number | null;
  mv_received_quantity: number | null;
  work_order_status: string | null;
  work_order_received_quantity: number | null;
  receive_required_quantity: number | null;
  wofg_complete: boolean;
  build_to_order: boolean;
  wireless_device_count: number;
  unassigned_wireless_count: number;
  wireless_portal_counts: PaperworkWirelessPortalCountRow[];
}

interface PaperworkSalesOrderShipmentBreakdown {
  shipment_id: number;
  shipped_quantity: number;
}

interface PaperworkSalesOrder {
  sales_order_id: number;
  sales_order_number: string;
  customer_purchase_order_number: string | null;
  client_name: string | null;
  sgs_ship_by_date: string | null;
  tracking_notification_sent: boolean;
  gosi_processed: boolean;
  gosi_sent: boolean;
  gosi_mv_id: number | null;
  gosi_document_number: string | null;
  gosi_status: string | null;
  gosi_date_created: string | null;
  gosi_terms: string | null;
  order_total_quantity: number | null;
  total_shipped_quantity: number;
  shipment_shipped_quantity: number;
  is_completely_shipped: boolean;
  shipment_breakdown: PaperworkSalesOrderShipmentBreakdown[];
  batch_items: PaperworkBatchItem[];
}

interface PaperworkShipment {
  shipment_id: number;
  sent_to_pack_out: boolean | null;
  batch_id: number | null;
  manufacturer_sales_order_number: string | null;
  date_shipped: string | null;
  date_ready: string | null;
  ready_for_pickup: boolean | null;
  ready_for_pickup_at: string | null;
  shipment_notification_sent: boolean;
  shipment_notification_sent_at: string | null;
  gosi_sent: boolean;
  gosi_sent_sales_order_numbers: string[];
  tracking_notification_sent_sales_order_numbers: string[];
  gosi_processed_sales_order_numbers: string[];
  mfr_invoice_processed: boolean;
  carrier: string | null;
  completion_override: boolean;
  note: string | null;
  tracking: string[];
  sales_orders: PaperworkSalesOrder[];
  batch_mfr_ship_by_date: string | null;
  batch_ship_to_recipient: string | null;
  wireless_device_count: number;
  unassigned_wireless_count: number;
  order_total_quantity: number | string | null;
  total_shipped_quantity: number | string;
  is_order_completely_shipped: boolean;
  batch_context_help: string | null;
}

interface PaperworkSalesOrderActionTarget {
  shipment: PaperworkShipment;
  salesOrder: PaperworkSalesOrder;
}

interface TrackingPreview {
  shipmentId: number;
  salesOrderNumber: string;
  recipientCandidate: string;
  internalRepEmail: string;
  externalRepEmail: string;
  subject: string;
  htmlBody: string;
  poNumber: string;
  poNumbers?: string[];
  trackingNumber: string[];
  trackingProvider: string;
  trackingUrl: string;
}

interface GosiSalesOrderContext {
  sales_order_number: string;
  customer_purchase_order_number: string | null;
  accounts_payable_email: string | null;
  has_gosi_document: boolean;
  gosi_document_number: string | null;
}

interface GosiInvoicePreview {
  shipmentId: number;
  salesOrderNumber: string;
  accountsPayableEmail: string;
  pilotOrder: boolean;
  subject: string;
  htmlBody: string;
  gosiDocumentNumber: string;
  gosiMvDocumentId: number;
  customerPurchaseOrderNumber: string | null;
}

const PAPERWORK_QUERY_KEY = ["shipping", "paperwork", "shipments"] as const;

/** Wait after expand/filter settles before warming work-order detail (avoids storms while typing search). */
const PAPERWORK_WORK_ORDER_PREFETCH_DEBOUNCE_MS = 2_000;

const PAPERWORK_PAGE_HOVER_HINT =
  "Paperwork queue after ready-for-pickup or ship. Expand a shipment for per–sales-order actions (send tracking, create GOSI, send invoice, fully shipped?) and a batch-item table with Process WO links. A shipment leaves the queue when every sales order has tracking sent and GOSI invoice sent, or you use completion override.";

const WIRELESS_PORTAL_LOOKUP_UNAVAILABLE_HINT =
  "The Wireless_Prod database is inaccessible in local development. Portal org and group counts load on the production hub only.";

function megaventoryGosiUrl(mvDocumentId: number, documentNumber: string): string {
  const trimmedNumber = documentNumber.trim();
  const name = encodeURIComponent(
    trimmedNumber !== "" ? `Goods Out and Sales Invoice ${trimmedNumber}` : `Goods Out and Sales Invoice ${mvDocumentId}`,
  );
  return `https://smartgridsolutions.megaventory.com/inventory/default.aspx?document=GoodsOutbound&id=${mvDocumentId}&name=${name}`;
}

const UNASSIGNED_WIRELESS_COLUMN_HINT =
  "Wireless devices on this line still assigned to the SGS Engineering portal org. Hover the count for portal org and group breakdown.";

function salesOrderActionKey(shipmentId: number, salesOrderNumber: string): string {
  return `${shipmentId}:${salesOrderNumber.trim()}`;
}

function gosiInvoiceSendButtonLabelForSalesOrder(salesOrder: PaperworkSalesOrder): string {
  return salesOrder.gosi_sent ? "Invoice sent" : "Send invoice";
}

function formatPaperworkGosiDate(value: string | null | undefined): string {
  if (value == null || value.trim() === "") return "—";
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (match) {
    const [, yyyy, mm, dd] = match;
    return `${mm}/${dd}/${yyyy}`;
  }
  return value.trim();
}

function formatPaperworkGosiTerms(value: string | null | undefined): string {
  if (value == null || value.trim() === "") return "—";
  const trimmed = value.trim();
  if (trimmed === "None") return "None";
  return mapMegaventoryPaymentTermsToLabel(trimmed, FALLBACK_MEGAVENTORY_PAYMENT_TERMS_LABELS) ?? trimmed;
}

function paperworkGosiPeekContext(salesOrder: PaperworkSalesOrder) {
  return {
    document_number: salesOrder.gosi_document_number,
    mv_document_id: salesOrder.gosi_mv_id,
    status: salesOrder.gosi_status,
    terms: formatPaperworkGosiTerms(salesOrder.gosi_terms),
    date_created: formatPaperworkGosiDate(salesOrder.gosi_date_created),
    sales_order_number: salesOrder.sales_order_number,
    customer_purchase_order_number: salesOrder.customer_purchase_order_number,
    client_name: salesOrder.client_name,
  };
}

function GosiCreatedCell({ salesOrder }: { salesOrder: PaperworkSalesOrder }) {
  if (!salesOrder.gosi_processed) {
    return null;
  }

  if (salesOrder.gosi_mv_id == null) {
    return (
      <HoverHintTrigger
        trigger={
          <span
            className={cn(
              "inline-flex h-8 items-center justify-center rounded-md border border-emerald-300 px-3 text-sm font-medium text-emerald-700",
              "whitespace-nowrap",
            )}
          >
            GOSI created
          </span>
        }
      >
        GOSI is marked processed on this shipment, but the synced invoice row is not loaded yet. Refresh Paperwork
        or open GOSI details and search by sales order.
      </HoverHintTrigger>
    );
  }

  return (
    <GosiDetailLink
      mvDocumentId={salesOrder.gosi_mv_id}
      gosiDocumentNumber={salesOrder.gosi_document_number}
      peek={paperworkGosiPeekContext(salesOrder)}
      className={cn(
        "inline-flex h-8 items-center justify-center rounded-md border border-emerald-300 bg-white px-3 text-sm font-medium text-emerald-700",
        "whitespace-nowrap hover:bg-emerald-50 hover:text-emerald-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1",
      )}
    >
      GOSI created
    </GosiDetailLink>
  );
}

async function fetchPaperworkShipments(): Promise<{
  shipments: PaperworkShipment[];
  wireless_portal_lookup_available: boolean;
}> {
  const data = await apiFetch<{
    shipments: PaperworkShipment[];
    wireless_portal_lookup_available: boolean;
  }>("/api/shipping/paperwork/shipments");
  return {
    shipments: data.shipments,
    wireless_portal_lookup_available: data.wireless_portal_lookup_available !== false,
  };
}

async function fetchTrackingPreview(shipmentId: number, salesOrderNumber: string): Promise<TrackingPreview> {
  const data = await apiFetch<{ preview: TrackingPreview }>(
    `/api/shipping/paperwork/shipments/${shipmentId}/tracking-preview`,
    { query: { salesOrderNumber } },
  );
  return data.preview;
}

async function sendTracking(
  shipmentId: number,
  salesOrderNumber: string,
  recipientEmail: string,
  ccEmails: string[],
): Promise<void> {
  await apiFetch(`/api/shipping/paperwork/shipments/${shipmentId}/send-tracking`, {
    method: "POST",
    body: { salesOrderNumber, recipientEmail, ccEmails },
  });
}

async function completeOverride(shipmentId: number, note: string): Promise<void> {
  await apiFetch(`/api/shipping/paperwork/shipments/${shipmentId}/completion-override`, {
    method: "POST",
    body: { note },
  });
}

async function patchShipmentNote(shipmentId: number, note: string): Promise<void> {
  await apiFetch(`/api/shipping/paperwork/shipments/${shipmentId}/note`, {
    method: "PATCH",
    body: { note },
  });
}

async function processGosi(
  shipmentId: number,
  salesOrderNumber: string,
  serviceChargeSkus: string[] = [],
): Promise<void> {
  await apiFetch(`/api/shipping/paperwork/shipments/${shipmentId}/process-gosi`, {
    method: "POST",
    body: { salesOrderNumber, serviceChargeSkus },
  });
}

interface GosiServiceChargeLinePreview {
  sku: string;
  description: string | null;
  quantity_ordered: number;
  quantity_already_invoiced: number;
  quantity_remaining: number;
  unit_price: number | null;
  line_total: number;
}

interface GosiServiceChargesPreview {
  shipment_id: number;
  sales_order_number: string;
  sales_order_id: number;
  product_fully_shipped: boolean;
  service_charges: GosiServiceChargeLinePreview[];
}

async function fetchGosiServiceChargesPreview(
  shipmentId: number,
  salesOrderNumber: string,
): Promise<GosiServiceChargesPreview> {
  const data = await apiFetch<{ preview: GosiServiceChargesPreview }>(
    `/api/shipping/paperwork/shipments/${shipmentId}/gosi-service-charges-preview`,
    { query: { salesOrderNumber } },
  );
  return data.preview;
}

function formatPaperworkCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(amount);
}

async function fetchGosiSalesOrders(shipmentId: number): Promise<GosiSalesOrderContext[]> {
  const data = await apiFetch<{ sales_orders: GosiSalesOrderContext[] }>(
    `/api/shipping/paperwork/shipments/${shipmentId}/gosi-sales-orders`,
  );
  return data.sales_orders;
}

async function fetchGosiInvoicePreview(shipmentId: number, salesOrderNumber: string): Promise<GosiInvoicePreview> {
  const data = await apiFetch<{ preview: GosiInvoicePreview }>(
    `/api/shipping/paperwork/shipments/${shipmentId}/gosi-invoice-preview`,
    { query: { salesOrderNumber } },
  );
  return data.preview;
}

async function sendGosiInvoice(
  shipmentId: number,
  salesOrderNumber: string,
  recipientEmail: string,
  ccEmails: string[],
): Promise<void> {
  await apiFetch(`/api/shipping/paperwork/shipments/${shipmentId}/send-gosi`, {
    method: "POST",
    body: { salesOrderNumber, recipientEmail, ccEmails },
  });
}

function formatDate(raw: string | null | undefined): string {
  if (!raw) return "-";
  const dateOnly = raw.slice(0, 10);
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateOnly);
  if (match) {
    return `${match[2]}/${match[3]}/${match[1]}`;
  }
  return raw;
}

function hasShipmentTracking(shipment: PaperworkShipment): boolean {
  return shipment.tracking.some((part) => typeof part === "string" && part.trim().length > 0);
}

function batchItemUnassignedWirelessHoverHint(item: PaperworkBatchItem): ReactNode {
  const unassignedCount = Number(item.unassigned_wireless_count ?? 0);
  const portalRows = item.wireless_portal_counts ?? [];
  const rows: Array<{ label: string; value: ReactNode }> = [
    {
      label: "Unassigned",
      value: <span className="tabular-nums">{Number.isFinite(unassignedCount) ? unassignedCount : 0}</span>,
    },
  ];
  for (const portalRow of portalRows) {
    rows.push({
      label: portalRow.portal_org_name,
      value: (
        <span className="tabular-nums">
          {portalRow.portal_group_names} — {portalRow.device_count}
        </span>
      ),
    });
  }
  if (portalRows.length === 0) {
    rows.push({
      label: "Portal org / group",
      value: "No portal org or group data for wireless devices on this line.",
    });
  }
  return <HoverHintDefinitionGrid rows={rows} />;
}

function BatchItemUnassignedWirelessCell({
  item,
  wirelessPortalLookupAvailable,
}: {
  item: PaperworkBatchItem;
  wirelessPortalLookupAvailable: boolean;
}) {
  const wirelessCount = Number(item.wireless_device_count ?? 0);
  if (!Number.isFinite(wirelessCount) || wirelessCount <= 0) {
    return <>—</>;
  }

  const unassignedCount = Number(item.unassigned_wireless_count ?? 0);
  const displayCount = Number.isFinite(unassignedCount) ? unassignedCount : 0;
  const hoverHint = wirelessPortalLookupAvailable
    ? batchItemUnassignedWirelessHoverHint(item)
    : WIRELESS_PORTAL_LOOKUP_UNAVAILABLE_HINT;

  if (!wirelessPortalLookupAvailable) {
    return (
      <HoverHintTrigger
        trigger={
          <CircleAlert
            className="mx-auto h-3.5 w-3.5 shrink-0 text-red-600"
            aria-label="Unassigned wireless count unavailable in local development"
          />
        }
      >
        {hoverHint}
      </HoverHintTrigger>
    );
  }

  return (
    <HoverHintTrigger trigger={<span className="tabular-nums">{displayCount}</span>}>{hoverHint}</HoverHintTrigger>
  );
}


function paperworkDetailField(value: string | null | undefined): string {
  const trimmed = value?.trim();
  return trimmed != null && trimmed !== "" ? trimmed : "—";
}

function BatchItemWorkOrderHoverHint({ item }: { item: PaperworkBatchItem }) {
  const required = item.receive_required_quantity ?? item.quantity;
  const received = item.work_order_received_quantity ?? item.mv_received_quantity ?? 0;
  return (
    <EntityPeekHint
      kind="work_order_inline"
      context={{
        build_order_number: item.build_order_number,
        mv_number: item.mv_number,
        mv_status: item.work_order_status ?? item.mv_status,
        received_for_shipment: { received, required: Number(required) },
      }}
    />
  );
}

function BatchItemSkuHoverHint({ item }: { item: PaperworkBatchItem }) {
  const sku = item.sku?.trim() || "—";
  const rows: Array<{ label: string; value: ReactNode }> = [
    { label: "Batch item", value: <span className="tabular-nums">{item.batch_item_id}</span> },
    { label: "SKU", value: sku },
  ];
  if (item.build_order_id != null) {
    rows.push(
      { label: "Build order", value: paperworkDetailField(item.build_order_number) },
      { label: "MV", value: paperworkDetailField(item.mv_number) },
    );
  }
  return <HoverHintDefinitionGrid rows={rows} />;
}

function SalesOrderCell({ salesOrder }: { salesOrder: PaperworkSalesOrder }) {
  return (
    <SalesOrderDetailLink
      salesOrderId={salesOrder.sales_order_id}
      salesOrderNumber={salesOrder.sales_order_number}
      className="text-sm font-medium"
      block
      peek={{
        sales_order_number: salesOrder.sales_order_number,
        client_name: salesOrder.client_name,
        customer_purchase_order_number: salesOrder.customer_purchase_order_number,
        sgs_ship_by_date: salesOrder.sgs_ship_by_date,
      }}
    />
  );
}

function SalesOrderFullyHoverHint({ salesOrder }: { salesOrder: PaperworkSalesOrder }) {
  const orderTotal = Number(salesOrder.order_total_quantity ?? 0);
  const rows = [...salesOrder.shipment_breakdown]
    .sort((left, right) => left.shipment_id - right.shipment_id)
    .map((row) => ({
      label: `Shipment ${row.shipment_id}`,
      value: (
        <span className="tabular-nums">
          {row.shipped_quantity}/{orderTotal}
        </span>
      ),
    }));
  rows.push({
    label: "Total",
    value: (
      <span className="tabular-nums font-medium">
        {Number(salesOrder.total_shipped_quantity ?? 0)}/{orderTotal}
      </span>
    ),
  });
  return <HoverHintDefinitionGrid rows={rows} />;
}

function carrierHoverHint(carrier: string, trackingNumbers: string[]): ReactNode {
  return <EntityPeekHint kind="carrier" context={{ carrier, tracking_numbers: trackingNumbers }} />;
}

function stopRowToggle(event: MouseEvent): void {
  event.stopPropagation();
}

function TruncatedHoverHintText({
  text,
  hoverHint,
  className,
  maxWidthClass = "max-w-[180px]",
  alwaysShowHover = false,
}: {
  text: string;
  hoverHint: ReactNode;
  className?: string;
  maxWidthClass?: string;
  alwaysShowHover?: boolean;
}) {
  const labelRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useLayoutEffect(() => {
    const labelElement = labelRef.current;
    if (!labelElement) return;

    const updateTruncation = () => {
      setIsTruncated(labelElement.scrollWidth > labelElement.clientWidth + 1);
    };

    updateTruncation();
    const resizeObserver = new ResizeObserver(updateTruncation);
    resizeObserver.observe(labelElement);
    return () => resizeObserver.disconnect();
  }, [text, maxWidthClass]);

  const label = (
    <span
      ref={labelRef}
      className={cn("block w-full min-w-0 truncate", maxWidthClass, className)}
    >
      {text}
    </span>
  );

  if (!isTruncated && !alwaysShowHover) {
    return <div className={cn("min-w-0", maxWidthClass)}>{label}</div>;
  }

  return (
    <HoverHintTrigger align="start" block className={maxWidthClass} trigger={label}>
      {typeof hoverHint === "string" ? (
        <span className="whitespace-pre-wrap break-words">{hoverHint}</span>
      ) : (
        hoverHint
      )}
    </HoverHintTrigger>
  );
}

function BatchItemSkuCell({ item }: { item: PaperworkBatchItem }) {
  const sku = item.sku?.trim() || "—";
  return (
    <HoverHintTrigger
      align="start"
      trigger={<span className="block min-w-0 truncate text-slate-700">{sku}</span>}
    >
      <BatchItemSkuHoverHint item={item} />
    </HoverHintTrigger>
  );
}

function trackingNumbersForCopy(shipment: PaperworkShipment): string {
  return shipment.tracking
    .map((part) => part.trim())
    .filter((part) => part.length > 0)
    .join("/");
}

function CarrierCell({ shipment }: { shipment: PaperworkShipment }) {
  const carrier = shipment.carrier?.trim() || "—";
  const trackingNumbers = shipment.tracking
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
  const hasTracking = hasShipmentTracking(shipment);

  return (
    <div className="flex min-w-0 items-center gap-1.5">
      <div className="min-w-0 flex-1 overflow-hidden">
        <TruncatedHoverHintText
          text={carrier}
          hoverHint={carrierHoverHint(carrier, trackingNumbers)}
          maxWidthClass="max-w-full"
          alwaysShowHover={hasTracking}
        />
      </div>
      <CopyToClipboardGlyph text={trackingNumbersForCopy(shipment)} label="tracking number" />
    </div>
  );
}

function WofgProcessButton({
  item,
  onOpenWorkOrder,
  onPrefetchWorkOrder,
}: {
  item: PaperworkBatchItem;
  onOpenWorkOrder: (buildOrderId: number) => void;
  onPrefetchWorkOrder: (buildOrderId: number) => void;
}) {
  if (item.build_order_id == null && item.build_to_order) {
    return (
      <ReadOnlyHoverLayer
        active
        hoverHint={`${item.sku} is made to order and this batch line has no work order. Create the work order on the batch before shipping or paperwork.`}
        layout="inline"
      >
        <Button size="sm" variant="secondary" disabled className="whitespace-nowrap border-red-300 text-red-700">
          Missing work order
        </Button>
      </ReadOnlyHoverLayer>
    );
  }
  if (item.build_order_id == null) {
    return (
      <ReadOnlyHoverLayer
        active
        hoverHint="This line is filled from stock; there is no work order to process."
        layout="inline"
      >
        <Button size="sm" variant="secondary" disabled className="whitespace-nowrap">
          From stock
        </Button>
      </ReadOnlyHoverLayer>
    );
  }
  const buildOrderId = item.build_order_id;
  const href = workOrderHubHref({
    id: buildOrderId,
    number: item.build_order_number ?? item.mv_number,
  });
  const anchorKey = paperworkProcessWoAnchorKey(buildOrderId);
  const label = item.wofg_complete ? "Done" : "Process WO";
  const link = (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      {...{ [PAPERWORK_PROCESS_WO_ANCHOR_ATTR]: anchorKey }}
      onPointerEnter={() => onPrefetchWorkOrder(buildOrderId)}
      onClick={(event) => {
        stopRowToggle(event);
        if (event.button !== 0) {
          return;
        }
        if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
          return;
        }
        event.preventDefault();
        onOpenWorkOrder(buildOrderId);
      }}
      onAuxClick={stopRowToggle}
      className={cn(
        "inline-flex h-8 items-center justify-center gap-2 rounded-md px-3 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2",
        item.wofg_complete
          ? "border border-emerald-300 bg-white text-emerald-700 hover:bg-slate-100"
          : "bg-brand-600 text-white hover:bg-brand-700",
        "whitespace-nowrap",
      )}
    >
      {label}
    </a>
  );
  return (
    <HoverHintTrigger align="start" closeOnTriggerActivate trigger={link}>
      <BatchItemWorkOrderHoverHint item={item} />
    </HoverHintTrigger>
  );
}

function PickupStatusBadge({ dateShipped }: { dateShipped: string | null }) {
  const pickedUp = Boolean(dateShipped);
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold ring-1",
        pickedUp
          ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
          : "bg-amber-50 text-amber-800 ring-amber-200",
      )}
    >
      {pickedUp ? <CheckCircle2 className="h-3 w-3 shrink-0" aria-hidden /> : <Package className="h-3 w-3 shrink-0" aria-hidden />}
      {pickedUp ? "Picked up" : "Not picked up"}
    </span>
  );
}

function trackingCcSeedEmails(preview: TrackingPreview): string[] {
  return [preview.externalRepEmail, preview.internalRepEmail].filter(
    (email): email is string => typeof email === "string" && email.trim().length > 0,
  );
}

async function ensureContactsForEmails(emails: string[]): Promise<HubContactSelection[]> {
  const uniqueEmails = [...new Set(emails.map((email) => email.trim().toLowerCase()).filter((email) => email.length > 0))];
  const contacts: HubContactSelection[] = [];
  for (const email of uniqueEmails) {
    const contact = await ensureHubContactSelection(
      hubContactSuggestionStub({ email, displayName: email }),
    );
    contacts.push(contact);
  }
  return contacts;
}

function FreshdeskPurchaseOrderSearchGlyphs({ poNumbers }: { poNumbers: string[] }) {
  if (poNumbers.length === 0) {
    return null;
  }

  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Freshdesk</p>
      <div className="mt-1.5 flex flex-wrap items-center gap-2">
        {poNumbers.map((purchaseOrderNumber) => {
          const ticketId = freshdeskTicketIdFromPurchaseOrder(purchaseOrderNumber);
          const opensDirectTicket = ticketId !== null;

          return (
            <HoverHintTrigger
              key={purchaseOrderNumber}
              align="start"
              trigger={
                <a
                  href={freshdeskPurchaseOrderAgentUrl(purchaseOrderNumber)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 transition-colors hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900"
                  aria-label={
                    opensDirectTicket
                      ? `Open Freshdesk ticket ${ticketId} for purchase order ${purchaseOrderNumber}`
                      : `Search Freshdesk for purchase order ${purchaseOrderNumber}`
                  }
                >
                  <ExternalLink className="h-4 w-4" aria-hidden />
                </a>
              }
            >
              <span className="break-words">
                {opensDirectTicket
                  ? `Open Freshdesk ticket ${ticketId} (purchase order ${purchaseOrderNumber}).`
                  : `Open Freshdesk ticket search for purchase order ${purchaseOrderNumber}.`}
              </span>
            </HoverHintTrigger>
          );
        })}
      </div>
    </div>
  );
}

function salesOrderFullyBadgeClasses(tone: "green" | "yellow"): string {
  return tone === "green"
    ? "bg-emerald-50 text-emerald-700"
    : "bg-amber-50 text-amber-800";
}

function salesOrderFullyDisplay(salesOrder: PaperworkSalesOrder): {
  label: "Yes" | "Partial";
  tone: "green" | "yellow";
} {
  const orderTotal = Number(salesOrder.order_total_quantity ?? 0);
  const onThisShipment = Number(salesOrder.shipment_shipped_quantity ?? 0);
  if (orderTotal <= 0) {
    return { label: "Partial", tone: "yellow" };
  }
  if (onThisShipment >= orderTotal) {
    return { label: "Yes", tone: "green" };
  }
  return {
    label: "Partial",
    tone: salesOrder.is_completely_shipped ? "green" : "yellow",
  };
}

function SalesOrderFullyCell({ salesOrder }: { salesOrder: PaperworkSalesOrder }) {
  const display = salesOrderFullyDisplay(salesOrder);
  return (
    <HoverHintTrigger
      align="center"
      trigger={
        <span
          className={cn(
            "inline-flex min-w-16 justify-center rounded px-2 py-1 text-xs font-semibold",
            salesOrderFullyBadgeClasses(display.tone),
          )}
        >
          {display.label}
        </span>
      }
    >
      <SalesOrderFullyHoverHint salesOrder={salesOrder} />
    </HoverHintTrigger>
  );
}

function ModalShell({
  children,
  maxWidth = "max-w-xl",
  onBackdrop,
}: {
  children: React.ReactNode;
  maxWidth?: string;
  onBackdrop?: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={(event) => {
        if (event.target === event.currentTarget) onBackdrop?.();
      }}
    >
      <div className={cn("max-h-[90vh] w-full overflow-hidden rounded-lg bg-white shadow-xl", maxWidth)}>
        {children}
      </div>
    </div>
  );
}

function SendTrackingModal({
  preview,
  previouslySent,
  onCancel,
  onSend,
  isSending,
  error,
}: {
  preview: TrackingPreview;
  previouslySent: boolean;
  onCancel: () => void;
  onSend: (recipientEmail: string, ccEmails: string[]) => void;
  isSending: boolean;
  error: string | null;
}) {
  const [recipientContact, setRecipientContact] = useState<HubContactSelection | null>(null);
  const [ccContacts, setCcContacts] = useState<HubContactSelection[]>([]);
  useEffect(() => {
    setRecipientContact(null);
    const seedEmails = trackingCcSeedEmails(preview);
    if (seedEmails.length === 0) {
      setCcContacts([]);
      return;
    }
    let cancelled = false;
    void ensureContactsForEmails(seedEmails).then((contacts) => {
      if (!cancelled) {
        setCcContacts(contacts);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [preview.shipmentId, preview.salesOrderNumber, preview.externalRepEmail, preview.internalRepEmail]);
  const sendDisabled =
    isSending || recipientContact == null;
  const purchaseOrderNumbers = purchaseOrderNumbersFromPreview(preview.poNumbers, preview.poNumber);

  return (
    <ModalShell maxWidth="max-w-4xl" onBackdrop={onCancel}>
      <div className="flex max-h-[90vh] flex-col">
        <div className="border-b border-slate-200 px-6 py-4">
          <h2 className="text-base font-semibold text-slate-900">
            {previouslySent ? "Resend tracking notification" : "Send tracking notification"}
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Shipment {preview.shipmentId} · {preview.salesOrderNumber} · PO {preview.poNumber || "-"} ·{" "}
            {preview.trackingProvider || "-"} {preview.trackingNumber.join(", ")}
          </p>
        </div>
        <div className="grid min-h-0 flex-1 gap-4 overflow-y-auto px-6 py-4 lg:grid-cols-[320px_1fr]">
          <div className="space-y-4">
            <div>
              <Label htmlFor="paperwork-recipient-email">PO sender</Label>
              <HubContactAutocomplete
                id="paperwork-recipient-email"
                contact={recipientContact}
                onContactChange={setRecipientContact}
                searchHint={preview.recipientCandidate}
                placeholder="Search contacts…"
                disabled={isSending}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="paperwork-cc-contacts">Carbon copy</Label>
              <HubContactTokenInput
                id="paperwork-cc-contacts"
                recipients={ccContacts}
                onRecipientsChange={setCcContacts}
                disabled={isSending}
                placeholder="Search contacts…"
                className="mt-1"
              />
            </div>
            <FreshdeskPurchaseOrderSearchGlyphs poNumbers={purchaseOrderNumbers} />
            <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Subject</p>
              <p className="mt-1 text-sm text-slate-800">{preview.subject}</p>
            </div>
            {error ? (
              <p className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                {error}
              </p>
            ) : null}
          </div>
          <div className="min-h-[520px] overflow-hidden rounded-md border border-slate-200 bg-white">
            <iframe
              title={`Tracking email preview for shipment ${preview.shipmentId}`}
              srcDoc={preview.htmlBody}
              className="h-full min-h-[520px] w-full"
            />
          </div>
        </div>
        <div className="flex justify-end gap-3 border-t border-slate-200 px-6 py-4">
          <Button variant="secondary" onClick={onCancel} disabled={isSending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={sendDisabled}
            onClick={() =>
              onSend(
                recipientContact!.email,
                ccContacts.map((contact) => contact.email),
              )
            }
          >
            {isSending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Send className="h-4 w-4" aria-hidden />}
            Send tracking
          </Button>
        </div>
      </div>
    </ModalShell>
  );
}

function SendGosiModal({
  shipment,
  salesOrders,
  preview,
  selectedSalesOrderNumber,
  onSelectSalesOrder,
  loadingPreview,
  onCancel,
  onSend,
  isSending,
  error,
}: {
  shipment: PaperworkShipment;
  salesOrders: GosiSalesOrderContext[];
  preview: GosiInvoicePreview | null;
  selectedSalesOrderNumber: string;
  onSelectSalesOrder: (salesOrderNumber: string) => void;
  loadingPreview: boolean;
  onCancel: () => void;
  onSend: (recipientEmail: string, ccEmails: string[]) => void;
  isSending: boolean;
  error: string | null;
}) {
  const [recipientContact, setRecipientContact] = useState<HubContactSelection | null>(null);
  useEffect(() => {
    setRecipientContact(null);
  }, [preview?.accountsPayableEmail, selectedSalesOrderNumber]);

  const pilotOrder = preview?.pilotOrder === true;
  const sendDisabled = isSending || loadingPreview || !preview || (!pilotOrder && recipientContact == null);

  return (
    <ModalShell maxWidth="max-w-4xl" onBackdrop={onCancel}>
      <div className="flex max-h-[90vh] flex-col">
        <div className="border-b border-slate-200 px-6 py-4">
          <h2 className="text-base font-semibold text-slate-900">Send GOSI invoice</h2>
          <p className="mt-1 text-sm text-slate-500">
            Shipment{" "}
            <ShipmentDetailLink shipmentId={shipment.shipment_id} className="font-medium tabular-nums">
              {shipment.shipment_id}
            </ShipmentDetailLink>
            {preview?.gosiDocumentNumber && preview.gosiMvDocumentId != null && Number.isFinite(preview.gosiMvDocumentId) ? (
              <>
                {" · "}
                <span className="inline-flex items-center gap-1">
                  <Link
                    to={buildShippingGosiDetailsPath(preview.gosiMvDocumentId, preview.gosiDocumentNumber)}
                    className="font-medium text-sky-700 hover:text-sky-900 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-1"
                  >
                    {preview.gosiDocumentNumber}
                  </Link>
                  <a
                    href={megaventoryGosiUrl(preview.gosiMvDocumentId, preview.gosiDocumentNumber)}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-sky-700 hover:text-sky-900"
                    aria-label={`Open GOSI ${preview.gosiDocumentNumber} in Megaventory`}
                  >
                    <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                  </a>
                </span>
              </>
            ) : preview?.gosiDocumentNumber ? (
              ` · ${preview.gosiDocumentNumber}`
            ) : null}
          </p>
        </div>
        <div className="grid min-h-0 flex-1 gap-4 overflow-y-auto px-6 py-4 lg:grid-cols-[320px_1fr]">
          <div className="space-y-4">
            {salesOrders.length > 1 ? (
              <div>
                <Label htmlFor="paperwork-gosi-sales-order">Sales order</Label>
                <select
                  id="paperwork-gosi-sales-order"
                  className="mt-1 h-9 w-full rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                  value={selectedSalesOrderNumber}
                  onChange={(event) => onSelectSalesOrder(event.target.value)}
                >
                  {salesOrders.map((row) => (
                    <option key={row.sales_order_number} value={row.sales_order_number}>
                      {row.sales_order_number}
                      {row.gosi_document_number ? ` (${row.gosi_document_number})` : ""}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
            {pilotOrder ? (
              <div>
                <Label>Pilot order</Label>
                <p className="mt-1 text-sm text-slate-800">orders@smartgridsolutions.com</p>
                <p className="mt-2 text-sm text-slate-700">
                  This was a pilot order. The invoice was not sent to the customer.
                </p>
              </div>
            ) : (
              <div>
                <Label htmlFor="paperwork-gosi-recipient-email">Accounts payable</Label>
                <HubContactAutocomplete
                  id="paperwork-gosi-recipient-email"
                  contact={recipientContact}
                  onContactChange={setRecipientContact}
                  searchHint={preview?.accountsPayableEmail ?? null}
                  placeholder="Search contacts…"
                  disabled={isSending || loadingPreview}
                  className="mt-1"
                />
              </div>
            )}
            <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Subject</p>
              <p className="mt-1 text-sm text-slate-800">{preview?.subject ?? "—"}</p>
            </div>
            {error ? (
              <p className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                {error}
              </p>
            ) : null}
          </div>
          <div className="min-h-[520px] overflow-hidden rounded-md border border-slate-200 bg-white">
            {loadingPreview ? (
              <div className="flex h-full min-h-[520px] items-center justify-center text-sm text-slate-500">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                Loading invoice preview…
              </div>
            ) : preview ? (
              <iframe
                title={`GOSI invoice preview for shipment ${shipment.shipment_id}`}
                srcDoc={preview.htmlBody}
                className="h-full min-h-[520px] w-full"
              />
            ) : (
              <div className="flex h-full min-h-[520px] items-center justify-center px-6 text-center text-sm text-slate-500">
                Select a sales order with a processed GOSI to preview the invoice email.
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-3 border-t border-slate-200 px-6 py-4">
          <Button variant="secondary" onClick={onCancel} disabled={isSending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={sendDisabled}
            onClick={() =>
              onSend(pilotOrder ? "orders@smartgridsolutions.com" : recipientContact!.email, [])
            }
          >
            {isSending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Send className="h-4 w-4" aria-hidden />}
            Send invoice
          </Button>
        </div>
      </div>
    </ModalShell>
  );
}

function GosiServiceChargesModal({
  target,
  preview,
  selectedSkus,
  onToggleSku,
  onCancel,
  onConfirm,
  isProcessing,
  error,
}: {
  target: PaperworkSalesOrderActionTarget;
  preview: GosiServiceChargesPreview;
  selectedSkus: ReadonlySet<string>;
  onToggleSku: (sku: string, checked: boolean) => void;
  onCancel: () => void;
  onConfirm: () => void;
  isProcessing: boolean;
  error: string | null;
}) {
  const selectedTotal = preview.service_charges
    .filter((line) => selectedSkus.has(line.sku))
    .reduce((sum, line) => sum + line.line_total, 0);

  return (
    <ModalShell maxWidth="max-w-lg" onBackdrop={onCancel}>
      <div className="flex max-h-[90vh] flex-col">
        <div className="border-b border-slate-200 px-6 py-4">
          <h2 className="text-base font-semibold text-slate-900">Include service charges on GOSI?</h2>
          <p className="mt-1 text-sm text-slate-500">
            Shipment {target.shipment.shipment_id} · {preview.sales_order_number}
          </p>
        </div>
        <div className="space-y-4 overflow-y-auto px-6 py-4">
          <p className="text-sm text-slate-700">
            This sales order has service charge lines (shipping, fees, tax, and similar) that are not tied to
            shipped product quantities. Choose which lines to add to the GOSI invoice.
          </p>
          {!preview.product_fully_shipped ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              Product quantities on this sales order are not fully shipped yet. Only include service charges now
              if you intend to invoice them on this partial shipment.
            </div>
          ) : null}
          <div className="overflow-hidden rounded-md border border-slate-200">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-2">Include</th>
                  <th className="px-3 py-2">SKU</th>
                  <th className="px-3 py-2 text-right">Qty</th>
                  <th className="px-3 py-2 text-right">Unit</th>
                  <th className="px-3 py-2 text-right">Line total</th>
                </tr>
              </thead>
              <tbody>
                {preview.service_charges.map((line) => {
                  const checked = selectedSkus.has(line.sku);
                  const description =
                    line.description != null && line.description.trim() !== "" ? line.description.trim() : null;
                  return (
                    <tr key={line.sku} className="border-t border-slate-200 text-slate-700">
                      <td className="px-3 py-2 align-top">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(event) => onToggleSku(line.sku, event.target.checked)}
                          aria-label={`Include ${line.sku} on GOSI`}
                        />
                      </td>
                      <td className="px-3 py-2 align-top">
                        <div className="font-medium text-slate-900">{line.sku}</div>
                        {description ? <div className="text-xs text-slate-500">{description}</div> : null}
                      </td>
                      <td className="px-3 py-2 text-right align-top tabular-nums">{line.quantity_remaining}</td>
                      <td className="px-3 py-2 text-right align-top tabular-nums">
                        {line.unit_price == null ? "-" : formatPaperworkCurrency(line.unit_price)}
                      </td>
                      <td className="px-3 py-2 text-right align-top tabular-nums">
                        {formatPaperworkCurrency(line.line_total)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-sm text-slate-600">
            Selected service charges total: <span className="font-medium">{formatPaperworkCurrency(selectedTotal)}</span>
          </p>
          {error ? <p className="text-sm text-red-600">{error}</p> : null}
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-200 px-6 py-4">
          <Button type="button" variant="secondary" onClick={onCancel} disabled={isProcessing}>
            Cancel
          </Button>
          <Button type="button" onClick={onConfirm} disabled={isProcessing}>
            <span className="relative inline-flex items-center justify-center">
              <span className={isProcessing ? "invisible" : undefined}>Create GOSI</span>
              {isProcessing ? <Loader2 className="absolute h-4 w-4 animate-spin" aria-hidden /> : null}
            </span>
          </Button>
        </div>
      </div>
    </ModalShell>
  );
}

function ConfirmResendTrackingModal({
  target,
  onCancel,
  onConfirm,
  isLoading,
}: {
  target: PaperworkSalesOrderActionTarget;
  onCancel: () => void;
  onConfirm: () => void;
  isLoading: boolean;
}) {
  return (
    <ModalShell maxWidth="max-w-md" onBackdrop={onCancel}>
      <div className="px-6 py-5">
        <h2 className="text-base font-semibold text-slate-900">Resend tracking email?</h2>
        <p className="mt-2 text-sm text-slate-600">
          Tracking was already sent for {target.salesOrder.sales_order_number} on shipment{" "}
          {target.shipment.shipment_id}. Send another notification?
        </p>
        <div className="mt-5 flex justify-end gap-3">
          <Button variant="secondary" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onConfirm} disabled={isLoading}>
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
            Continue
          </Button>
        </div>
      </div>
    </ModalShell>
  );
}

function EditPaperworkNoteModal({
  shipment,
  onCancel,
  onSave,
  isSaving,
  error,
}: {
  shipment: PaperworkShipment;
  onCancel: () => void;
  onSave: (note: string) => void;
  isSaving: boolean;
  error: string | null;
}) {
  const [note, setNote] = useState(shipment.note ?? "");
  const noteFieldRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const frameId = requestAnimationFrame(() => {
      noteFieldRef.current?.focus();
    });
    return () => cancelAnimationFrame(frameId);
  }, []);

  return (
    <ModalShell maxWidth="max-w-lg" onBackdrop={onCancel}>
      <div className="px-6 py-5">
        <h2 className="text-base font-semibold text-slate-900">Paperwork note · Shipment {shipment.shipment_id}</h2>
        <div className="mt-4">
          <Label htmlFor="paperwork-edit-note">Note</Label>
          <p id="paperwork-edit-note-help" className="mt-1 text-xs text-slate-500">
            Optional context for anyone working this row (customer call, hold reason, link to ticket). This is separate
            from completion override, which records why a row was removed from the queue.
          </p>
          <TextArea
            ref={noteFieldRef}
            id="paperwork-edit-note"
            aria-describedby="paperwork-edit-note-help"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder="Add a note…"
            className="mt-2 font-sans"
          />
        </div>
        {error ? (
          <p className="mt-3 flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            {error}
          </p>
        ) : null}
        <div className="mt-5 flex justify-end gap-3">
          <Button variant="secondary" onClick={onCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => onSave(note)} disabled={isSaving}>
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
            Save note
          </Button>
        </div>
      </div>
    </ModalShell>
  );
}

function CompletionOverrideModal({
  shipment,
  onCancel,
  onConfirm,
  isSaving,
  error,
}: {
  shipment: PaperworkShipment;
  onCancel: () => void;
  onConfirm: (note: string) => void;
  isSaving: boolean;
  error: string | null;
}) {
  const [note, setNote] = useState("");
  return (
    <ModalShell maxWidth="max-w-lg" onBackdrop={onCancel}>
      <div className="px-6 py-5">
        <h2 className="text-base font-semibold text-slate-900">Completion override</h2>
        <p className="mt-2 text-sm text-slate-600">
          Hide shipment {shipment.shipment_id} from Paperwork without completing the remaining status checks.
        </p>
        <div className="mt-4">
          <Label htmlFor="paperwork-completion-note">Reason for override</Label>
          <p id="paperwork-completion-note-help" className="mt-1 text-xs text-slate-500">
            Required audit text. Saving replaces the paperwork note field on this shipment with what you enter here.
          </p>
          <TextArea
            id="paperwork-completion-note"
            aria-describedby="paperwork-completion-note-help"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder="Why should this shipment leave the paperwork queue?"
            className="mt-2 font-sans"
          />
        </div>
        {error ? (
          <p className="mt-3 flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            {error}
          </p>
        ) : null}
        <div className="mt-5 flex justify-end gap-3">
          <Button variant="secondary" onClick={onCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => onConfirm(note.trim())} disabled={isSaving || note.trim() === ""}>
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
            Confirm override
          </Button>
        </div>
      </div>
    </ModalShell>
  );
}

function PaperworkBatchItemsTable({
  items,
  wirelessPortalLookupAvailable,
  onOpenWorkOrder,
  onPrefetchWorkOrder,
}: {
  items: PaperworkBatchItem[];
  wirelessPortalLookupAvailable: boolean;
  onOpenWorkOrder: (buildOrderId: number) => void;
  onPrefetchWorkOrder: (buildOrderId: number) => void;
}) {
  if (items.length === 0) {
    return null;
  }

  return (
    <div className="border-t border-slate-200 bg-slate-50/80 px-3 py-2">
      <table className="w-full min-w-[420px] border-collapse text-left text-sm">
        <thead className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          <tr>
            <th className="py-1.5 pl-3 pr-4">SKU</th>
            <th className="px-3 py-1.5 text-right tabular-nums">Qty</th>
            <th className="px-3 py-1.5 text-center tabular-nums">
              <HoverHintTrigger trigger="Unassigned wireless">{UNASSIGNED_WIRELESS_COLUMN_HINT}</HoverHintTrigger>
            </th>
            <th className="py-1.5 pl-3 pr-3">Process WO</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {items.map((item) => (
            <tr key={item.batch_item_id} className="bg-white text-slate-700">
              <td className="py-1.5 pl-3 pr-4">
                <BatchItemSkuCell item={item} />
              </td>
              <td className="px-3 py-1.5 text-right tabular-nums">{item.quantity}</td>
              <td className="px-3 py-1.5 text-center tabular-nums">
                <BatchItemUnassignedWirelessCell
                  item={item}
                  wirelessPortalLookupAvailable={wirelessPortalLookupAvailable}
                />
              </td>
              <td className="py-1.5 pl-3 pr-3">
                <WofgProcessButton
                  item={item}
                  onOpenWorkOrder={onOpenWorkOrder}
                  onPrefetchWorkOrder={onPrefetchWorkOrder}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PaperworkShipmentLinesTable({
  shipment,
  wirelessPortalLookupAvailable,
  canSendTracking,
  loadingPreviewKey,
  loadingGosiPreviewKey,
  processingGosiKeys,
  sendGosiMutationPending,
  onSendTrackingClick,
  onProcessGosi,
  onOpenGosiSendWorkflow,
  onOpenWorkOrder,
  onPrefetchWorkOrder,
}: {
  shipment: PaperworkShipment;
  wirelessPortalLookupAvailable: boolean;
  canSendTracking: boolean;
  loadingPreviewKey: string | null;
  loadingGosiPreviewKey: string | null;
  processingGosiKeys: ReadonlySet<string>;
  sendGosiMutationPending: boolean;
  onSendTrackingClick: (target: PaperworkSalesOrderActionTarget) => void;
  onProcessGosi: (target: PaperworkSalesOrderActionTarget) => void;
  onOpenGosiSendWorkflow: (target: PaperworkSalesOrderActionTarget) => void;
  onOpenWorkOrder: (buildOrderId: number) => void;
  onPrefetchWorkOrder: (buildOrderId: number) => void;
}) {
  const salesOrders = shipment.sales_orders ?? [];
  if (salesOrders.length === 0) {
    return (
      <p className="px-4 py-3 text-sm text-slate-500">No sales orders on this shipment.</p>
    );
  }

  return (
    <div className="space-y-3 border-t border-slate-100 bg-slate-50/50 px-4 py-3">
      {salesOrders.map((salesOrder) => {
        const target: PaperworkSalesOrderActionTarget = { shipment, salesOrder };
        const actionKey = salesOrderActionKey(shipment.shipment_id, salesOrder.sales_order_number);
        const loadingPreview = loadingPreviewKey === actionKey;
        const loadingGosiPreview = loadingGosiPreviewKey === actionKey;
        const processingGosi = processingGosiKeys.has(actionKey);
        const batchItems = salesOrder.batch_items ?? [];
        const missingMadeToOrder = batchItems.filter(
          (item) => item.build_to_order && item.build_order_id == null,
        );
        const missingWorkOrderHint =
          missingMadeToOrder.length === 0
            ? ""
            : `${missingMadeToOrder.map((item) => item.sku).join(", ")} ${
                missingMadeToOrder.length === 1 ? "is" : "are"
              } made to order and ${
                missingMadeToOrder.length === 1 ? "has" : "have"
              } no work order. Create the work order on the batch before shipping or paperwork.`;
        const buildOrdersBlockingGosi = batchItems
          .filter((item) => item.build_order_id != null && !item.wofg_complete)
          .map((item) => item.build_order_number?.trim() || `build order ${item.build_order_id}`)
          .filter((label, index, labels) => labels.indexOf(label) === index);

        return (
          <div
            key={`${shipment.shipment_id}-so-${salesOrder.sales_order_id}`}
            className="overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm"
          >
            <table className="w-full min-w-[640px] border-collapse text-left text-sm">
              <thead className="border-b border-slate-200 bg-slate-50/90 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="py-2 pl-3 pr-4">Sales order</th>
                  <th className="px-3 py-2 text-center">Fully?</th>
                  <th className="px-3 py-2">Send tracking</th>
                  <th className="px-3 py-2">Create GOSI</th>
                  <th className="px-3 py-2">Send invoice</th>
                </tr>
              </thead>
              <tbody>
                <tr className="text-slate-700">
                  <td className="py-2 pl-3 pr-4">
                    <SalesOrderCell salesOrder={salesOrder} />
                  </td>
                  <td className="px-3 py-2 text-center">
                    <SalesOrderFullyCell salesOrder={salesOrder} />
                  </td>
                  <td className="px-3 py-2">
                    <ReadOnlyHoverLayer
                      active={!loadingPreview && !canSendTracking}
                      hoverHint="Add a tracking number on the shipment before sending tracking email."
                      layout="inline"
                    >
                      <Button
                        size="sm"
                        variant={salesOrder.tracking_notification_sent ? "secondary" : "primary"}
                        onClick={(event) => {
                          stopRowToggle(event);
                          onSendTrackingClick(target);
                        }}
                        disabled={loadingPreview || !canSendTracking}
                        className={cn(
                          salesOrder.tracking_notification_sent ? "border-emerald-300 text-emerald-700" : undefined,
                          "whitespace-nowrap",
                        )}
                      >
                        <span className="relative inline-flex items-center justify-center">
                          <span className={loadingPreview ? "invisible" : undefined}>
                            {salesOrder.tracking_notification_sent ? "Tracking sent" : "Send tracking"}
                          </span>
                          {loadingPreview ? (
                            <Loader2 className="absolute h-3.5 w-3.5 animate-spin" aria-hidden />
                          ) : null}
                        </span>
                      </Button>
                    </ReadOnlyHoverLayer>
                  </td>
                  <td className="px-3 py-2">
                    {salesOrder.gosi_processed ? (
                      <GosiCreatedCell salesOrder={salesOrder} />
                    ) : (
                      <ReadOnlyHoverLayer
                        active={missingWorkOrderHint !== "" || buildOrdersBlockingGosi.length > 0}
                        hoverHint={
                          missingWorkOrderHint !== ""
                            ? missingWorkOrderHint
                            : `Receive ${buildOrdersBlockingGosi.join(", ")} before creating the sales invoice.`
                        }
                        layout="inline"
                      >
                      <Button
                        size="sm"
                        variant="primary"
                        disabled={
                          processingGosi
                          || !shipment.date_shipped
                          || missingWorkOrderHint !== ""
                          || buildOrdersBlockingGosi.length > 0
                        }
                        onClick={(event) => {
                          stopRowToggle(event);
                          void onProcessGosi(target);
                        }}
                        className="whitespace-nowrap"
                      >
                        <span className="relative inline-flex items-center justify-center">
                          <span className={processingGosi ? "invisible" : undefined}>Create GOSI</span>
                          {processingGosi ? (
                            <Loader2 className="absolute h-3.5 w-3.5 animate-spin" aria-hidden />
                          ) : null}
                        </span>
                      </Button>
                      </ReadOnlyHoverLayer>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <Button
                      size="sm"
                      variant={salesOrder.gosi_sent ? "secondary" : "primary"}
                      disabled={!salesOrder.gosi_processed || loadingGosiPreview || sendGosiMutationPending}
                      onClick={(event) => {
                        stopRowToggle(event);
                        void onOpenGosiSendWorkflow(target);
                      }}
                      className={cn(
                        salesOrder.gosi_sent ? "border-emerald-300 text-emerald-700" : undefined,
                        "whitespace-nowrap",
                      )}
                    >
                      <span className="relative inline-flex items-center justify-center">
                        <span className={loadingGosiPreview ? "invisible" : undefined}>
                          {gosiInvoiceSendButtonLabelForSalesOrder(salesOrder)}
                        </span>
                        {loadingGosiPreview ? (
                          <Loader2 className="absolute h-3.5 w-3.5 animate-spin" aria-hidden />
                        ) : null}
                      </span>
                    </Button>
                  </td>
                </tr>
              </tbody>
            </table>
            <PaperworkBatchItemsTable
              items={batchItems}
              wirelessPortalLookupAvailable={wirelessPortalLookupAvailable}
              onOpenWorkOrder={onOpenWorkOrder}
              onPrefetchWorkOrder={onPrefetchWorkOrder}
            />
          </div>
        );
      })}
    </div>
  );
}

export function PaperworkPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [preview, setPreview] = useState<TrackingPreview | null>(null);
  const [sendTarget, setSendTarget] = useState<PaperworkSalesOrderActionTarget | null>(null);
  const [resendTarget, setResendTarget] = useState<PaperworkSalesOrderActionTarget | null>(null);
  const [completionTarget, setCompletionTarget] = useState<PaperworkShipment | null>(null);
  const [noteEditTarget, setNoteEditTarget] = useState<PaperworkShipment | null>(null);
  const [loadingPreviewKey, setLoadingPreviewKey] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const [completionError, setCompletionError] = useState<string | null>(null);
  const [noteEditError, setNoteEditError] = useState<string | null>(null);
  const [gosiSendTarget, setGosiSendTarget] = useState<PaperworkSalesOrderActionTarget | null>(null);
  const [gosiSalesOrders, setGosiSalesOrders] = useState<GosiSalesOrderContext[]>([]);
  const [gosiPreview, setGosiPreview] = useState<GosiInvoicePreview | null>(null);
  const [gosiSelectedSalesOrder, setGosiSelectedSalesOrder] = useState("");
  const [loadingGosiPreviewKey, setLoadingGosiPreviewKey] = useState<string | null>(null);
  const [gosiSendError, setGosiSendError] = useState<string | null>(null);
  const [gosiCreateTarget, setGosiCreateTarget] = useState<PaperworkSalesOrderActionTarget | null>(null);
  const [gosiServiceChargesPreview, setGosiServiceChargesPreview] = useState<GosiServiceChargesPreview | null>(
    null,
  );
  const [selectedGosiServiceChargeSkus, setSelectedGosiServiceChargeSkus] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [gosiCreateError, setGosiCreateError] = useState<string | null>(null);
  const [processingGosiKeys, setProcessingGosiKeys] = useState<ReadonlySet<string>>(() => new Set());
  const [expandedShipmentIds, setExpandedShipmentIds] = useState<Record<number, boolean>>({});
  const [workOrderCalloutId, setWorkOrderCalloutId] = useState<number | null>(null);

  const shipmentsQuery = useQuery({
    queryKey: PAPERWORK_QUERY_KEY,
    queryFn: fetchPaperworkShipments,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const sendTrackingMutation = useMutation({
    mutationFn: ({
      shipmentId,
      salesOrderNumber,
      recipientEmail,
      ccEmails,
    }: {
      shipmentId: number;
      salesOrderNumber: string;
      recipientEmail: string;
      ccEmails: string[];
    }) => sendTracking(shipmentId, salesOrderNumber, recipientEmail, ccEmails),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PAPERWORK_QUERY_KEY });
      setPreview(null);
      setSendTarget(null);
      setSendError(null);
      setActionError(null);
    },
    onError: (error) => {
      setSendError(error instanceof ApiError ? error.message : String(error));
    },
  });

  const completionMutation = useMutation({
    mutationFn: ({ shipmentId, note }: { shipmentId: number; note: string }) => completeOverride(shipmentId, note),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PAPERWORK_QUERY_KEY });
      setCompletionTarget(null);
      setCompletionError(null);
      setActionError(null);
    },
    onError: (error) => {
      setCompletionError(error instanceof ApiError ? error.message : String(error));
    },
  });

  const processGosiMutation = useMutation({
    mutationFn: ({
      shipmentId,
      salesOrderNumber,
      serviceChargeSkus,
    }: {
      shipmentId: number;
      salesOrderNumber: string;
      serviceChargeSkus: string[];
    }) => processGosi(shipmentId, salesOrderNumber, serviceChargeSkus),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PAPERWORK_QUERY_KEY });
      setActionError(null);
      setGosiCreateTarget(null);
      setGosiServiceChargesPreview(null);
      setSelectedGosiServiceChargeSkus(new Set());
      setGosiCreateError(null);
    },
    onError: (error) => {
      const message = error instanceof ApiError ? error.message : String(error);
      setGosiCreateError(message);
      setActionError(message);
    },
  });

  const sendGosiMutation = useMutation({
    mutationFn: ({
      shipmentId,
      salesOrderNumber,
      recipientEmail,
      ccEmails,
    }: {
      shipmentId: number;
      salesOrderNumber: string;
      recipientEmail: string;
      ccEmails: string[];
    }) => sendGosiInvoice(shipmentId, salesOrderNumber, recipientEmail, ccEmails),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PAPERWORK_QUERY_KEY });
      setGosiSendTarget(null);
      setGosiPreview(null);
      setGosiSalesOrders([]);
      setGosiSendError(null);
      setActionError(null);
    },
    onError: (error) => {
      setGosiSendError(error instanceof ApiError ? error.message : String(error));
    },
  });

  const noteMutation = useMutation({
    mutationFn: ({ shipmentId, note }: { shipmentId: number; note: string }) => patchShipmentNote(shipmentId, note),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PAPERWORK_QUERY_KEY });
      setNoteEditTarget(null);
      setNoteEditError(null);
      setActionError(null);
    },
    onError: (error) => {
      setNoteEditError(error instanceof ApiError ? error.message : String(error));
    },
  });

  const shipments = shipmentsQuery.data?.shipments ?? [];
  const wirelessPortalLookupAvailable = shipmentsQuery.data?.wireless_portal_lookup_available ?? false;
  const filteredShipments = useMemo(
    () => filterPaperworkShipments(shipments, search),
    [search, shipments],
  );

  useEffect(() => {
    let cancelled = false;
    const timer = window.setTimeout(() => {
      void (async () => {
        for (const shipment of filteredShipments) {
          if (cancelled) {
            return;
          }
          if (!(expandedShipmentIds[shipment.shipment_id] ?? false)) {
            continue;
          }
          await prefetchWorkOrdersForPaperworkShipment(queryClient, shipment);
        }
      })();
    }, PAPERWORK_WORK_ORDER_PREFETCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [expandedShipmentIds, filteredShipments, queryClient]);

  function isShipmentExpanded(shipmentId: number): boolean {
    return expandedShipmentIds[shipmentId] ?? false;
  }

  function toggleShipmentExpanded(shipmentId: number): void {
    setExpandedShipmentIds((previous) => ({
      ...previous,
      [shipmentId]: !(previous[shipmentId] ?? false),
    }));
  }

  async function loadGosiPreview(target: PaperworkSalesOrderActionTarget, salesOrderNumber: string) {
    setGosiSendError(null);
    const key = salesOrderActionKey(target.shipment.shipment_id, salesOrderNumber);
    setLoadingGosiPreviewKey(key);
    try {
      const nextPreview = await fetchGosiInvoicePreview(target.shipment.shipment_id, salesOrderNumber);
      setGosiPreview(nextPreview);
    } catch (error) {
      setGosiSendError(error instanceof ApiError ? error.message : String(error));
      setGosiPreview(null);
    } finally {
      setLoadingGosiPreviewKey(null);
    }
  }

  async function openGosiSendWorkflow(target: PaperworkSalesOrderActionTarget) {
    setActionError(null);
    setGosiSendError(null);
    const key = salesOrderActionKey(target.shipment.shipment_id, target.salesOrder.sales_order_number);
    setLoadingGosiPreviewKey(key);
    try {
      const salesOrders = await fetchGosiSalesOrders(target.shipment.shipment_id);
      const row = salesOrders.find((entry) => entry.sales_order_number === target.salesOrder.sales_order_number);
      if (!row?.has_gosi_document) {
        setActionError("Create GOSI before sending the invoice.");
        return;
      }
      if (target.salesOrder.gosi_sent) {
        setActionError("This sales order invoice was already sent.");
        return;
      }
      setGosiSalesOrders([row]);
      setGosiSelectedSalesOrder(target.salesOrder.sales_order_number);
      setGosiSendTarget(target);
      await loadGosiPreview(target, target.salesOrder.sales_order_number);
    } catch (error) {
      setActionError(error instanceof ApiError ? error.message : String(error));
    } finally {
      setLoadingGosiPreviewKey(null);
    }
  }

  async function handleProcessGosi(target: PaperworkSalesOrderActionTarget) {
    setActionError(null);
    setGosiCreateError(null);
    const key = salesOrderActionKey(target.shipment.shipment_id, target.salesOrder.sales_order_number);
    setProcessingGosiKeys((previous) => {
      if (previous.has(key)) {
        return previous;
      }
      const next = new Set(previous);
      next.add(key);
      return next;
    });
    try {
      const preview = await fetchGosiServiceChargesPreview(
        target.shipment.shipment_id,
        target.salesOrder.sales_order_number,
      );
      if (preview.service_charges.length === 0) {
        await processGosiMutation.mutateAsync({
          shipmentId: target.shipment.shipment_id,
          salesOrderNumber: target.salesOrder.sales_order_number,
          serviceChargeSkus: [],
        });
        return;
      }
      setGosiCreateTarget(target);
      setGosiServiceChargesPreview(preview);
      setSelectedGosiServiceChargeSkus(new Set(preview.service_charges.map((line) => line.sku)));
    } catch (error) {
      setActionError(error instanceof ApiError ? error.message : String(error));
    } finally {
      setProcessingGosiKeys((previous) => {
        if (!previous.has(key)) {
          return previous;
        }
        const next = new Set(previous);
        next.delete(key);
        return next;
      });
    }
  }

  async function confirmGosiCreateWithServiceCharges() {
    if (!gosiCreateTarget) {
      return;
    }
    setGosiCreateError(null);
    await processGosiMutation.mutateAsync({
      shipmentId: gosiCreateTarget.shipment.shipment_id,
      salesOrderNumber: gosiCreateTarget.salesOrder.sales_order_number,
      serviceChargeSkus: [...selectedGosiServiceChargeSkus],
    });
  }

  async function openTrackingWorkflow(target: PaperworkSalesOrderActionTarget, forceResend: boolean) {
    setActionError(null);
    setSendError(null);
    const key = salesOrderActionKey(target.shipment.shipment_id, target.salesOrder.sales_order_number);
    setLoadingPreviewKey(key);
    try {
      const nextPreview = await fetchTrackingPreview(
        target.shipment.shipment_id,
        target.salesOrder.sales_order_number,
      );
      setPreview(nextPreview);
      setSendTarget(target);
      if (forceResend) {
        setResendTarget(null);
      }
    } catch (error) {
      setActionError(error instanceof ApiError ? error.message : String(error));
    } finally {
      setLoadingPreviewKey(null);
    }
  }

  function handleSendTrackingClick(target: PaperworkSalesOrderActionTarget) {
    if (!hasShipmentTracking(target.shipment)) {
      return;
    }
    if (target.salesOrder.tracking_notification_sent) {
      setResendTarget(target);
      return;
    }
    void openTrackingWorkflow(target, false);
  }

  const loadError = shipmentsQuery.isError && shipmentsQuery.error instanceof Error
    ? shipmentsQuery.error.message
    : null;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <PageTitleWithHelp hoverHint={PAPERWORK_PAGE_HOVER_HINT}>Paperwork</PageTitleWithHelp>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => void shipmentsQuery.refetch()}
          disabled={shipmentsQuery.isFetching}
        >
          {shipmentsQuery.isFetching ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
          ) : (
            <RefreshCw className="h-3.5 w-3.5" aria-hidden />
          )}
          Refresh
        </Button>
      </header>

      <Card>
        <CardBody className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative min-w-[280px] flex-1">
              <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" aria-hidden />
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search or ship:2120, batch:2050, so:GSO-…, sku:, po:, @ship …"
                className="pl-9"
              />
            </div>
          </div>

          {actionError ? (
            <p className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              {actionError}
            </p>
          ) : null}
          {loadError ? (
            <p className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              Could not load shipments: {loadError}
            </p>
          ) : null}

          {shipmentsQuery.isLoading ? (
            <div className="flex items-center gap-2 py-8 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              Loading paperwork shipments...
            </div>
          ) : filteredShipments.length === 0 ? (
            <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
              No paperwork shipments match the current filters.
            </div>
          ) : (
            <div className="max-h-[min(70vh,720px)] overflow-auto rounded-md border border-slate-200">
              <table className="min-w-[800px] w-full table-fixed border-collapse text-left text-sm">
                <colgroup>
                  <col className="w-[6rem]" />
                  <col className="w-[5.5rem]" />
                  <col className="w-[5.5rem]" />
                  <col className="w-[7rem]" />
                  <col className="w-[9rem]" />
                  <col className="w-[3rem]" />
                  <col className="w-[6.5rem]" />
                </colgroup>
                <thead className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50/95 text-xs font-semibold uppercase tracking-wide text-slate-500 backdrop-blur-sm">
                  <tr>
                    <th className="py-2 pl-4 pr-3">Shipment</th>
                    <th className="px-3 py-2">Batch</th>
                    <th className="px-3 py-2">Shipped</th>
                    <th className="px-3 py-2">Pickup</th>
                    <th className="px-3 py-2">Carrier</th>
                    <th className="px-3 py-2 text-center">Note</th>
                    <th className="py-2 pl-3 pr-4">Override</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredShipments.map((shipment) => {
                    const expanded = isShipmentExpanded(shipment.shipment_id);
                    const canSendTracking = hasShipmentTracking(shipment);

                    return (
                      <Fragment key={shipment.shipment_id}>
                        <tr
                          aria-expanded={expanded}
                          tabIndex={0}
                          onClick={() => toggleShipmentExpanded(shipment.shipment_id)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              toggleShipmentExpanded(shipment.shipment_id);
                            }
                          }}
                          className="cursor-pointer text-slate-700 hover:bg-slate-50/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500"
                        >
                          <td className="py-2 pl-4 pr-3 font-medium text-slate-900">
                            <div className="flex items-center gap-1">
                              <span className="inline-flex shrink-0 text-slate-500" aria-hidden>
                                {expanded ? (
                                  <ChevronDown className="h-4 w-4" />
                                ) : (
                                  <ChevronRight className="h-4 w-4" />
                                )}
                              </span>
                              <span onClick={stopRowToggle}>
                                <ShipmentDetailLink shipmentId={shipment.shipment_id} className="tabular-nums">
                                  {shipment.shipment_id}
                                </ShipmentDetailLink>
                              </span>
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            {shipment.batch_id != null ? (
                              <span onClick={stopRowToggle}>
                                <BatchDetailLink batchId={shipment.batch_id} className="tabular-nums">
                                  {shipment.batch_id}
                                </BatchDetailLink>
                              </span>
                            ) : (
                              <span className="tabular-nums">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">{formatDate(shipment.date_shipped)}</td>
                          <td className="px-3 py-2">
                            <PickupStatusBadge dateShipped={shipment.date_shipped} />
                          </td>
                          <td className="min-w-0 overflow-hidden px-3 py-2">
                            <CarrierCell shipment={shipment} />
                          </td>
                          <td className="px-3 py-2 text-center">
                            {shipment.note?.trim() ? (
                              <HoverHintTrigger
                                align="start"
                                trigger={
                                  <button
                                    type="button"
                                    className="inline-flex rounded p-1 text-slate-600 hover:bg-slate-100 hover:text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-1"
                                    aria-label={`Paperwork note for shipment ${shipment.shipment_id}. Hover to read; click to edit.`}
                                    onClick={(event) => {
                                      stopRowToggle(event);
                                      setNoteEditError(null);
                                      setNoteEditTarget(shipment);
                                    }}
                                  >
                                    <FileText className="h-4 w-4 shrink-0" aria-hidden />
                                  </button>
                                }
                              >
                                <HoverHintDefinitionGrid
                                  rows={[
                                    {
                                      label: "Note",
                                      value: (
                                        <span className="whitespace-pre-wrap">{shipment.note?.trim()}</span>
                                      ),
                                    },
                                  ]}
                                />
                              </HoverHintTrigger>
                            ) : (
                              <button
                                type="button"
                                className="inline-flex rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-1"
                                aria-label={`Add paperwork note for shipment ${shipment.shipment_id}`}
                                onClick={(event) => {
                                  stopRowToggle(event);
                                  setNoteEditError(null);
                                  setNoteEditTarget(shipment);
                                }}
                              >
                                <Pencil className="h-4 w-4 shrink-0" aria-hidden />
                              </button>
                            )}
                          </td>
                          <td className="py-2 pl-3 pr-4">
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={(event) => {
                                stopRowToggle(event);
                                setCompletionError(null);
                                setCompletionTarget(shipment);
                              }}
                            >
                              Override
                            </Button>
                          </td>
                        </tr>
                        {expanded ? (
                          <tr className="bg-slate-50/30">
                            <td colSpan={7} className="p-0">
                              <PaperworkShipmentLinesTable
                                shipment={shipment}
                                wirelessPortalLookupAvailable={wirelessPortalLookupAvailable}
                                canSendTracking={canSendTracking}
                                loadingPreviewKey={loadingPreviewKey}
                                loadingGosiPreviewKey={loadingGosiPreviewKey}
                                processingGosiKeys={processingGosiKeys}
                                sendGosiMutationPending={sendGosiMutation.isPending}
                                onSendTrackingClick={handleSendTrackingClick}
                                onProcessGosi={handleProcessGosi}
                                onOpenGosiSendWorkflow={openGosiSendWorkflow}
                                onOpenWorkOrder={setWorkOrderCalloutId}
                                onPrefetchWorkOrder={(buildOrderId) =>
                                  prefetchWorkOrderDetail(queryClient, buildOrderId)
                                }
                              />
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {resendTarget ? (
        <ConfirmResendTrackingModal
          target={resendTarget}
          isLoading={
            loadingPreviewKey
              === salesOrderActionKey(resendTarget.shipment.shipment_id, resendTarget.salesOrder.sales_order_number)
          }
          onCancel={() => setResendTarget(null)}
          onConfirm={() => void openTrackingWorkflow(resendTarget, true)}
        />
      ) : null}

      {preview && sendTarget ? (
        <SendTrackingModal
          preview={preview}
          previouslySent={sendTarget.salesOrder.tracking_notification_sent}
          isSending={sendTrackingMutation.isPending}
          error={sendError}
          onCancel={() => {
            setPreview(null);
            setSendTarget(null);
            setSendError(null);
          }}
          onSend={(recipientEmail, ccEmails) =>
            sendTrackingMutation.mutate({
              shipmentId: preview.shipmentId,
              salesOrderNumber: preview.salesOrderNumber,
              recipientEmail,
              ccEmails,
            })
          }
        />
      ) : null}

      {gosiSendTarget ? (
        <SendGosiModal
          shipment={gosiSendTarget.shipment}
          salesOrders={gosiSalesOrders}
          preview={gosiPreview}
          selectedSalesOrderNumber={gosiSelectedSalesOrder}
          loadingPreview={
            loadingGosiPreviewKey
              === salesOrderActionKey(gosiSendTarget.shipment.shipment_id, gosiSendTarget.salesOrder.sales_order_number)
          }
          isSending={sendGosiMutation.isPending}
          error={gosiSendError}
          onCancel={() => {
            setGosiSendTarget(null);
            setGosiPreview(null);
            setGosiSalesOrders([]);
            setGosiSendError(null);
          }}
          onSelectSalesOrder={(salesOrderNumber) => {
            setGosiSelectedSalesOrder(salesOrderNumber);
            void loadGosiPreview(gosiSendTarget, salesOrderNumber);
          }}
          onSend={(recipientEmail, ccEmails) =>
            sendGosiMutation.mutate({
              shipmentId: gosiSendTarget.shipment.shipment_id,
              salesOrderNumber: gosiSelectedSalesOrder,
              recipientEmail,
              ccEmails,
            })
          }
        />
      ) : null}

      {gosiCreateTarget && gosiServiceChargesPreview ? (
        <GosiServiceChargesModal
          target={gosiCreateTarget}
          preview={gosiServiceChargesPreview}
          selectedSkus={selectedGosiServiceChargeSkus}
          onToggleSku={(sku, checked) => {
            setSelectedGosiServiceChargeSkus((previous) => {
              const next = new Set(previous);
              if (checked) {
                next.add(sku);
              } else {
                next.delete(sku);
              }
              return next;
            });
          }}
          onCancel={() => {
            if (processGosiMutation.isPending) {
              return;
            }
            setGosiCreateTarget(null);
            setGosiServiceChargesPreview(null);
            setSelectedGosiServiceChargeSkus(new Set());
            setGosiCreateError(null);
          }}
          onConfirm={() => {
            void confirmGosiCreateWithServiceCharges();
          }}
          isProcessing={processGosiMutation.isPending}
          error={gosiCreateError}
        />
      ) : null}

      {completionTarget ? (
        <CompletionOverrideModal
          shipment={completionTarget}
          isSaving={completionMutation.isPending}
          error={completionError}
          onCancel={() => {
            setCompletionTarget(null);
            setCompletionError(null);
          }}
          onConfirm={(note) =>
            completionMutation.mutate({
              shipmentId: completionTarget.shipment_id,
              note,
            })
          }
        />
      ) : null}

      {noteEditTarget ? (
        <EditPaperworkNoteModal
          key={noteEditTarget.shipment_id}
          shipment={noteEditTarget}
          isSaving={noteMutation.isPending}
          error={noteEditError}
          onCancel={() => {
            setNoteEditTarget(null);
            setNoteEditError(null);
          }}
          onSave={(note) =>
            noteMutation.mutate({
              shipmentId: noteEditTarget.shipment_id,
              note,
            })
          }
        />
      ) : null}

      {workOrderCalloutId != null ? (
        <WorkOrderDetailAnchoredCallout
          buildOrderId={workOrderCalloutId}
          anchorKey={paperworkProcessWoAnchorKey(workOrderCalloutId)}
          onClose={() => setWorkOrderCalloutId(null)}
          onMutated={() => {
            void queryClient.invalidateQueries({ queryKey: PAPERWORK_QUERY_KEY });
          }}
        />
      ) : null}

      {sendTrackingMutation.isSuccess ? (
        <div className="sr-only" aria-live="polite">
          Tracking notification sent.
        </div>
      ) : null}
      {completionMutation.isSuccess ? (
        <div className="sr-only" aria-live="polite">
          Completion override saved.
        </div>
      ) : null}
      <div className="hidden">
        <XCircle aria-hidden />
      </div>
    </div>
  );
}
